import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { LocaleService } from '@sb-shared/services/locale.service';
import { CookieService } from 'ngx-cookie-service';
import { ActivationService } from '@sb-core/services/activation.service';
import { ToastService } from '@sb-core/services/toast.service';
import { Language } from '@sb-shared/models/language';
import { Buttons } from '@sb-shared/models/UI/buttons';
import { LanguageCodeService } from '@sb-shared/services/language-code.service';

@Component({
    selector: 'app-activation',
    templateUrl: './activation.component.html',
    styleUrl: './activation.component.scss',
    standalone: false
})
export class ActivationComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private localeService = inject(LocaleService);
  private cookiesService = inject(CookieService);
  private activationService = inject(ActivationService);
  private toastService = inject(ToastService);
  private languageCode = inject(LanguageCodeService);
  private cookieName = 'Anonymous_Default_Language';
  private domainCookieName = 'Organisation_Domain';
  private defaultLanguage = 'en-gb';

  form: FormGroup;
  resetPasswordToken!: string;
  currentYear = new Date().getFullYear();
  userName?: string;
  toastMessage: string = '';
  showToast: boolean = false;
  toastType: 'success' | 'error' = 'success';
  loading: boolean = true;
  languages: Language[];
  languageItems: Buttons;
  selectedLanguage: Language;
  orgId: number = 0;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            this.uppercaseValidator,
            this.lowercaseValidator,
            this.digitValidator,
            this.specialCharValidator,
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: this.passwordsMatchValidator,
        updateOn: 'change',
      } as AbstractControlOptions
    );
  }

  ngOnInit(): void {
    const email = this.route.snapshot.queryParamMap.get('email');
    const token = this.route.snapshot.queryParamMap.get('t') as string;
    this.orgId = Number(this.route.snapshot.queryParamMap.get('oId'));
    this.userName = email;
    
    this.form.get('email')?.setValue(email);
    let languageCode = this.cookiesService.get(this.cookieName);
    let language = this.defaultLanguage;
    if(languageCode && languageCode != '') {
      language = JSON.parse(languageCode); 
    }
    else {
      this.cookiesService.set(this.cookieName, JSON.stringify(this.defaultLanguage));
    }

    this.localeService.setOrganisationLanguage(this.orgId, language)
    .subscribe(x => {
      this.getLanguages();
      
      this.loading = false;
      if (email && token) {
        this.activationService
          .ValidateResetToken(email, token)
          .subscribe({
            next: ({ userId, isValid, domain }) => {  
              if (!isValid) {
                this.router.navigate(['/ExpiredToken'], { queryParams: { email: email, oId: this.orgId } });
              } else {
                this.resetPasswordToken = token;
                this.cookiesService.set(this.domainCookieName, domain);
              }
            },
            error: (err) => this.toastService.getError('Token validation failed. Please try again.', 'Error'),
          });
      } else {
        this.router.navigate(['/ExpiredToken'], { queryParams: { email: email, oId: this.orgId } });
      }
    });    
  }

  onSubmit() {
    if (this.form.valid) {
      const { email, password } = this.form.value;
      const isWelcome = !!this.route.snapshot.queryParamMap.get('isWelcome');

      this.activationService
        .ResetPassword(email, password, this.resetPasswordToken)
        .subscribe({
          next: (res) => {
            if(res.isSuccess) {
              this.activationService
                .QueueWelcomeCommunication(this.orgId, true, email)
                .subscribe();
              
              this.router.navigate(['/WelcomeRedirect'], { queryParams: { isWelcome: isWelcome } });
            }
            else {
              this.toastService.getError('Password reset failed. Please try again.', 'Error');
            }
          },
          error: (err) => {
            this.toastService.getError('Password reset failed. Please try again.', 'Error');
            console.log({ err });
          },
        });
    }
  }

  onButtonClick(index?: number) {
      const language = this.languages[index];
      this.saveLanguage(language);
  }

  backgroundImageUrl(): string {
    return `url(${window.EveryBuddy.WebAPI}/img/backgrounds/3.png)`;
  }
  
  private getLanguages() {
    forkJoin([
      this.languageCode.getLanguageCodesForOrganisation(this.orgId, true)
    ]).subscribe(([languages]) => {
      this.languages = languages;
      const currentLanguage = this.findLanguageByCode(languages, JSON.parse(this.cookiesService.get(this.cookieName)))
      this.getLanguageItems();
      this.updateLanguage(currentLanguage);
    });
  }

  private getLanguageItems() {
    this.languageItems = this.languages.map(language => {
      return {
        message: language.languageTitle,
        imgUrl: language.imgUrl,
        buttonId: language.languageCode,
        isActive: language === this.selectedLanguage
      }
    });
  }

  private saveLanguage(newLanguage: Language) {
    this.localeService.setOrganisationLanguage(this.orgId, this.formatLanguage(newLanguage))
      .subscribe(() => {
        this.updateLanguage(newLanguage);
      });
  }

  private updateLanguage(newLanguage: Language): void {
    this.selectedLanguage = this.languages.find(language => language === newLanguage);
    this.languageItems = this.languageItems.map((item, idx) => ({
      ...item,
      isActive: this.languages[idx] === this.selectedLanguage
    }));
  }

  formatLanguage(locale: Language) {
    return locale.languageCode + '-' + locale.countryCode;
  }

  private findLanguageByCode(languages: Language[], languageCode: string): Language {
    // Selected language
    return languages.find(language => this.formatLanguage(language) === languageCode)
      // Default language
      || languages.find(language => language.isDefault);
  }

  hasPasswordError(errorType: string) {
    const passwordControl = this.form.get('password');

    return passwordControl?.hasError(errorType);
  }

  private uppercaseValidator(control: AbstractControl): ValidationErrors | null {
    return /[A-Z]/.test(control.value) ? null : { uppercase: true };
  }

  private lowercaseValidator(control: AbstractControl): ValidationErrors | null {
    return /[a-z]/.test(control.value) ? null : { lowercase: true };
  }

  private digitValidator(control: AbstractControl): ValidationErrors | null {
    return /\d/.test(control.value) ? null : { digit: true };
  }

  private specialCharValidator(control: AbstractControl): ValidationErrors | null {
    return /[!£$%^&*()#~]/.test(control.value) ? null : { specialChar: true };
  }

  private passwordsMatchValidator(group: FormGroup): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }
}
